import {
  InputGroup,
  InputGroupDropDown,
  TextField,
  Toggle,
} from "@certn/cascadia-react";
import { Text } from "@certn/cascadia";
import { formatCurrency } from "@certn/utils";
import { useFormikContext } from "formik";
import { useTranslation } from "react-i18next";

import { useAllowedCurrencyForSubsidiary } from "../../hooks/useAllowedCurrencyForSubsidiary";
import { useClientAccount } from "../../hooks/useClientAccount";
import { Subsidiary } from "../../types";
import { formatMaximumPrices } from "../criminal-record-templates/utils";
import type { AddressBasedTemplateFormValues } from "./types";

interface MaximumPriceFieldsProps {
  isDisabled?: boolean;
}

function currencyLabel(currency: string) {
  const symbol = formatCurrency(0, undefined, currency, 0)
    .replace("0", "")
    .trim();
  return `${currency} ${symbol}`;
}

export function MaximumPriceFields({
  isDisabled = false,
}: MaximumPriceFieldsProps) {
  const { t } = useTranslation();
  const { client } = useClientAccount();
  const allowedCurrencies = useAllowedCurrencyForSubsidiary(
    client?.subsidiary as Subsidiary,
    true
  );
  const { values, setFieldValue, errors, submitCount } =
    useFormikContext<AddressBasedTemplateFormValues>();
  const maximumPriceError =
    submitCount > 0 && typeof errors.maximum_price === "string"
      ? errors.maximum_price
      : undefined;

  const maximumPrice = values.maximum_price ?? {};
  const selectedCurrency =
    Object.keys(maximumPrice)[0] ?? allowedCurrencies[0]?.id ?? "CAD";
  const amount = maximumPrice[selectedCurrency];

  return (
    <>
      <Text style="form/section-heading">{t("Price Controls")}</Text>
      <Text style="input/helper" color="text-weak">
        {t(
          "The maximum total price for all countries triggered by this template. If the estimated prices exceed this threshold, checks will be ordered starting from the applicant's most recent addresses until the limit is reached."
        )}
      </Text>
      <Toggle
        isSelected={values.enable_maximum_price ?? false}
        onChange={(selected) => {
          setFieldValue("enable_maximum_price", selected);
          if (!selected) {
            setFieldValue("maximum_price", {});
          }
        }}
        testID="enable-maximum-price"
        isDisabled={isDisabled}
      >
        {t("Enable maximum price cap")}
      </Toggle>
      {values.enable_maximum_price &&
        (isDisabled ? (
          <TextField
            label={t("Price threshold")}
            value={formatMaximumPrices(values.maximum_price)}
            isDisabled
            testID="maximum-price-readonly"
          />
        ) : (
          <InputGroup
            name={`maximum_price.${selectedCurrency}`}
            label={t("Price threshold")}
            type="text"
            placeholder="0"
            testID="maximum-price-input"
            isInvalid={Boolean(maximumPriceError)}
            errors={maximumPriceError}
            value={
              amount
                ? formatCurrency(amount, undefined, selectedCurrency, 0)
                : ""
            }
            onChange={(value) => {
              // NOTE(INTCRIMDB): the API only accepts whole amounts, so drop
              // decimals instead of sending a float like the criminal record page.
              // The field is formatted as en-US, so "." is the decimal separator
              // and the fraction must be cut before stripping it ("25.50" -> 25).
              const [digits] = value.replace(/[^0-9.]/g, "").split(".");
              setFieldValue("maximum_price", {
                [selectedCurrency]: digits ? parseInt(digits, 10) : undefined,
              });
            }}
            dropdown={
              <InputGroupDropDown
                selectedKey={currencyLabel(selectedCurrency)}
                onSelectionChange={(currency) => {
                  setFieldValue("maximum_price", {
                    [currency as string]: amount,
                  });
                }}
                items={allowedCurrencies.map((currency) => ({
                  value: currency.id,
                  display: currencyLabel(currency.id),
                }))}
                testID="currency-dropdown"
              />
            }
          />
        ))}
    </>
  );
}
